import { createFileRoute, useParams } from '@tanstack/react-router';
import { useState } from 'react';

import { fmt, fmtDate, toNumber, dateToInputValue } from '@dcc-erp/shared';
import type { VendorStatement } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { PageHead } from '../components/layout';
import { useActiveCompanyId } from '../lib/queries';
import { useVendorStatement } from '../lib/queries/ap';
import { downloadCertPdf } from '../lib/queries/wht';
import { exportRows, todayStamp, type ExportColumn } from '../lib/xlsx';

// AP Phase C5 — Vendor 360 / statement. A per-vendor read-only view aggregating
// the vendor master, headline KPIs and five sections (bills / payments / aging /
// 50-ทวิ certs / credit-debit notes). Reachable from the AP bill drawer's vendor
// name. On-screen + xlsx export (no PDF). Mirrors the AR customer-statement
// page; plain prototype.css classes only.

export const Route = createFileRoute('/ap/vendor/$vendorId')({
  component: VendorStatementPage,
});

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'รออนุมัติ',
  POSTED: 'รอจ่าย',
  PAID: 'จ่ายแล้ว',
  VOID: 'ยกเลิก',
};

export function VendorStatementPage() {
  const { vendorId } = useParams({ from: '/ap/vendor/$vendorId' });
  const companyId = useActiveCompanyId();
  const today = new Date();
  const [asOf, setAsOf] = useState<string>(dateToInputValue(today));

  const { data, isPending, error } = useVendorStatement(companyId, vendorId, asOf || undefined);

  // ส่งออก (xlsx) — a two-sheet workbook (bills + payments) of the statement.
  function handleExport(): void {
    if (!data) return;
    const billCols: ExportColumn<VendorStatement['bills'][number]>[] = [
      { header: 'เลขที่', value: (b) => b.voucherNo, width: 18 },
      { header: 'เลขที่ใบกำกับ', value: (b) => b.vendorInvoiceNo ?? '', width: 18 },
      { header: 'ลงวันที่', value: (b) => fmtDate(b.billDate), width: 14 },
      { header: 'ครบกำหนด', value: (b) => fmtDate(b.dueDate), width: 14 },
      { header: 'สถานะ', value: (b) => STATUS_LABEL[b.status] ?? b.status, width: 12 },
      { header: 'ยอดรวม', value: (b) => toNumber(b.totalAmount), width: 14 },
      { header: 'จ่ายแล้ว', value: (b) => toNumber(b.paidAmount), width: 14 },
      { header: 'คงค้าง', value: (b) => toNumber(b.outstanding), width: 14 },
      { header: 'อายุหนี้ (วัน)', value: (b) => b.agingDays, width: 12 },
    ];
    const fileName = `vendor-statement_${data.vendor.code}_${todayStamp()}.xlsx`;
    // exportRows builds a single-sheet workbook; for the simplest dependable
    // export we ship the bills sheet (the primary statement grid). The page also
    // renders payments/certs/notes on screen.
    void exportRows(fileName, 'ใบแจ้งยอด', billCols, data.bills);
  }

  return (
    <div className="page">
      <PageHead
        crumbs={['เจ้าหนี้การค้า', 'ข้อมูลผู้ขาย 360']}
        title="ข้อมูลผู้ขาย 360 (Vendor Statement)"
      />

      <div className="tbl-shell">
        {/* Controls — as-of date + xlsx export */}
        <div
          className="filter-row"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span className="muted">ณ วันที่</span>
            <input
              className="input w-sm"
              type="date"
              aria-label="ณ วันที่"
              value={asOf}
              onChange={(e) => setAsOf(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="btn"
            disabled={!data}
            onClick={handleExport}
            aria-label="ส่งออก (xlsx)"
          >
            <Icon name="download" size={14} /> ส่งออก (xlsx)
          </button>
        </div>

        {isPending && (
          <div className="muted" style={{ padding: 24, textAlign: 'center' }}>
            กำลังโหลด…
          </div>
        )}
        {error && (
          <div className="neg" style={{ padding: 24, textAlign: 'center' }}>
            โหลดไม่สำเร็จ: {error.message}
          </div>
        )}

        {data && (
          <>
            <VendorHeader data={data} />
            <KpiRow data={data} />
            <BillsSection data={data} />
            <PaymentsSection data={data} />
            <AgingSection data={data} />
            <CertsSection data={data} companyId={companyId} />
            <NotesSection data={data} />
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function VendorHeader({ data }: { data: VendorStatement }) {
  const v = data.vendor;
  return (
    <div className="card" style={{ marginBottom: 12, padding: 12 }}>
      <div style={{ fontWeight: 600 }}>
        <span className="code">{v.code}</span> {v.nameTh}
      </div>
      <div className="muted" style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginTop: 4 }}>
        <span>เลขประจำตัวผู้เสียภาษี: {v.taxId ?? '—'}</span>
        <span>เงื่อนไขชำระ: {v.terms ?? v.defaultPaymentTermCode ?? '—'}</span>
        <span>อีเมล: {v.email ?? '—'}</span>
      </div>
      {v.addressTh && (
        <div className="muted" style={{ marginTop: 4 }}>
          ที่อยู่: {v.addressTh}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function KpiRow({ data }: { data: VendorStatement }) {
  const k = data.kpis;
  const cards: { label: string; value: string; tone?: string }[] = [
    { label: 'ยอดซื้อสะสม', value: fmt(toNumber(k.billed), 2) },
    { label: 'จ่ายแล้ว', value: fmt(toNumber(k.paid), 2) },
    { label: 'คงค้าง', value: fmt(toNumber(k.outstanding), 2) },
    { label: 'เกินกำหนด', value: fmt(toNumber(k.overdue), 2), tone: 'neg' },
    { label: 'DPO (วัน)', value: k.dpo == null ? '—' : fmt(toNumber(k.dpo), 1) },
  ];
  return (
    <div
      data-testid="vendor-kpis"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: 12,
        margin: '0 0 16px',
      }}
    >
      {cards.map((c) => (
        <div key={c.label} className="card" style={{ padding: 12 }}>
          <div className="muted" style={{ fontSize: 12 }}>
            {c.label}
          </div>
          <div className={`tnum ${c.tone ?? ''}`} style={{ fontSize: 18, fontWeight: 600 }}>
            {c.value}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
function BillsSection({ data }: { data: VendorStatement }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div className="section-title" style={{ margin: '0 0 8px' }}>
        ใบแจ้งหนี้ ({data.bills.length})
      </div>
      <div className="tbl-wrap" style={{ maxHeight: 420 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>เลขที่</th>
              <th>เลขที่ใบกำกับ</th>
              <th>ลงวันที่</th>
              <th>ครบกำหนด</th>
              <th>สถานะ</th>
              <th className="num">ยอดรวม</th>
              <th className="num">จ่ายแล้ว</th>
              <th className="num">คงค้าง</th>
              <th className="num">อายุหนี้</th>
            </tr>
          </thead>
          <tbody>
            {data.bills.length === 0 ? (
              <tr>
                <td colSpan={9} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  ไม่มีใบแจ้งหนี้
                </td>
              </tr>
            ) : (
              data.bills.map((b) => (
                <tr key={b.id}>
                  <td className="code">{b.voucherNo}</td>
                  <td>{b.vendorInvoiceNo ?? '—'}</td>
                  <td>{fmtDate(b.billDate)}</td>
                  <td>{fmtDate(b.dueDate)}</td>
                  <td>{STATUS_LABEL[b.status] ?? b.status}</td>
                  <td className="num tnum">{fmt(toNumber(b.totalAmount), 2)}</td>
                  <td className="num tnum">
                    {toNumber(b.paidAmount) ? fmt(toNumber(b.paidAmount), 2) : '—'}
                  </td>
                  <td className="num tnum">
                    <b>{fmt(toNumber(b.outstanding), 2)}</b>
                  </td>
                  <td className="num tnum">{b.agingDays}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function PaymentsSection({ data }: { data: VendorStatement }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div className="section-title" style={{ margin: '0 0 8px' }}>
        การจ่าย ({data.payments.length})
      </div>
      <div className="tbl-wrap" style={{ maxHeight: 360 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>เลขที่</th>
              <th>วันที่</th>
              <th>วิธีจ่าย</th>
              <th className="num">ยอดรวม</th>
              <th className="num">ภาษีหัก ณ ที่จ่าย</th>
              <th className="num">จ่ายสุทธิ</th>
            </tr>
          </thead>
          <tbody>
            {data.payments.length === 0 ? (
              <tr>
                <td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  ยังไม่มีการจ่าย
                </td>
              </tr>
            ) : (
              data.payments.map((p) => (
                <tr key={p.id}>
                  <td className="code">{p.voucherNo}</td>
                  <td>{fmtDate(p.paymentDate)}</td>
                  <td>{p.paymentMethod ?? '—'}</td>
                  <td className="num tnum">{fmt(toNumber(p.totalAmount), 2)}</td>
                  <td className="num tnum">
                    {toNumber(p.whtTotal) ? fmt(toNumber(p.whtTotal), 2) : '—'}
                  </td>
                  <td className="num tnum">{fmt(toNumber(p.netPaid ?? p.totalAmount), 2)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
const BUCKET_LABEL: Record<string, string> = {
  notDue: 'ยังไม่ครบกำหนด',
  d1_30: '1-30 วัน',
  d31_60: '31-60 วัน',
  d61_90: '61-90 วัน',
  over90: '90+ วัน',
};

function AgingSection({ data }: { data: VendorStatement }) {
  const a = data.aging;
  const grand =
    toNumber(a.notDue) +
    toNumber(a.d1_30) +
    toNumber(a.d31_60) +
    toNumber(a.d61_90) +
    toNumber(a.over90);
  return (
    <div style={{ marginBottom: 16 }}>
      <div className="section-title" style={{ margin: '0 0 8px' }}>
        อายุหนี้
      </div>
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>{BUCKET_LABEL.notDue}</th>
              <th>{BUCKET_LABEL.d1_30}</th>
              <th>{BUCKET_LABEL.d31_60}</th>
              <th>{BUCKET_LABEL.d61_90}</th>
              <th>{BUCKET_LABEL.over90}</th>
              <th className="num">รวม</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="num tnum">{fmt(toNumber(a.notDue), 2)}</td>
              <td className="num tnum">{fmt(toNumber(a.d1_30), 2)}</td>
              <td className="num tnum">{fmt(toNumber(a.d31_60), 2)}</td>
              <td className="num tnum">{fmt(toNumber(a.d61_90), 2)}</td>
              <td className="num tnum">{fmt(toNumber(a.over90), 2)}</td>
              <td className="num tnum">{fmt(grand, 2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function CertsSection({
  data,
  companyId,
}: {
  data: VendorStatement;
  companyId: string | undefined;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div className="section-title" style={{ margin: '0 0 8px' }}>
        ใบ 50 ทวิ ({data.certs.length})
      </div>
      <div className="tbl-wrap" style={{ maxHeight: 360 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>เลขที่</th>
              <th>วันที่</th>
              <th>ประเภทเงินได้</th>
              <th className="num">ฐานภาษี</th>
              <th className="num">อัตรา</th>
              <th className="num">ภาษีหัก</th>
              <th style={{ minWidth: 160 }} />
            </tr>
          </thead>
          <tbody>
            {data.certs.length === 0 ? (
              <tr>
                <td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  ยังไม่มีใบ 50 ทวิ
                </td>
              </tr>
            ) : (
              data.certs.map((c) => (
                <tr key={c.id}>
                  <td className="code">{c.voucherNo}</td>
                  <td>{fmtDate(c.certDate)}</td>
                  <td>{c.incomeType}</td>
                  <td className="num tnum">{fmt(toNumber(c.base), 2)}</td>
                  <td className="num tnum">{fmt(toNumber(c.rate), 2)}%</td>
                  <td className="num tnum">{fmt(toNumber(c.amount), 2)}</td>
                  <td>
                    {c.channel === 'E_WHT' ? (
                      <span className="meta" style={{ fontSize: 13 }}>
                        ออกโดยธนาคาร (e-WHT)
                      </span>
                    ) : (
                      <button
                        className="btn sm ghost"
                        onClick={() => companyId && void downloadCertPdf(companyId, c.id)}
                      >
                        <Icon name="print" size={12} /> พิมพ์ 50 ทวิ
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
const NOTE_STATUS_LABEL: Record<string, string> = {
  DRAFT: 'ร่าง',
  POSTED: 'ลงบัญชีแล้ว',
  VOID: 'ยกเลิก',
};

function NotesSection({ data }: { data: VendorStatement }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div className="section-title" style={{ margin: '0 0 8px' }}>
        ใบลดหนี้ / ใบเพิ่มหนี้ ({data.notes.length})
      </div>
      <div className="tbl-wrap" style={{ maxHeight: 360 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>เลขที่</th>
              <th>ประเภท</th>
              <th>วันที่</th>
              <th>อ้างอิงใบแจ้งหนี้</th>
              <th className="num">ยอดรวม</th>
              <th>สถานะ</th>
            </tr>
          </thead>
          <tbody>
            {data.notes.length === 0 ? (
              <tr>
                <td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  ยังไม่มีใบลดหนี้ / ใบเพิ่มหนี้
                </td>
              </tr>
            ) : (
              data.notes.map((n) => (
                <tr key={n.id}>
                  <td className="code">{n.voucherNo}</td>
                  <td>{n.noteType === 'CREDIT' ? 'ลดหนี้' : 'เพิ่มหนี้'}</td>
                  <td>{fmtDate(n.noteDate)}</td>
                  <td className="code">{n.billVoucherNo ?? '—'}</td>
                  <td className="num tnum">{fmt(toNumber(n.totalAmount), 2)}</td>
                  <td>{NOTE_STATUS_LABEL[n.status] ?? n.status}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
